import React, { useId } from 'react';
import { cn } from '../../../lib/utils';

export interface OptifitJointPipeSvgProps {
  maxHiloMm?: number | null;
  redFlag?: boolean | null;
  className?: string;
  onPrev?: () => void;
  onNext?: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
}

/**
 * Read-only illustration matching the Optifit field tablet joint view:
 * two 3-D pipes with circular end caps, centre chrome joint band,
 * green solid line on the left barrel, yellow dashed line on the right,
 * nav chevrons, status pill.
 */
export const OptifitJointPipeSvg: React.FC<OptifitJointPipeSvgProps> = ({
  maxHiloMm,
  redFlag,
  className,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
}) => {
  const rid = useId().replace(/:/g, '');
  const pipeBodyGrad = `pipeBodyGrad-${rid}`;
  const pipeHighlight = `pipeHighlight-${rid}`;
  const capGrad = `capGrad-${rid}`;
  const jointGrad = `jointGrad-${rid}`;
  const jointHighlight = `jointHighlight-${rid}`;
  const shadowGrad = `shadowGrad-${rid}`;
  const leftPipeClip = `leftPipeClip-${rid}`;
  const rightPipeClip = `rightPipeClip-${rid}`;

  const hiloText = (() => {
    if (maxHiloMm == null || !Number.isFinite(Number(maxHiloMm))) return '—';
    const n = Number(maxHiloMm);
    const rounded = Math.round(n * 100) / 100;
    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2);
  })();
  const showOptimal = redFlag !== true;

  return (
    <div className={cn('relative w-full min-w-0', className)}>
      <svg
        viewBox="0 0 720 220"
        preserveAspectRatio="xMidYMid meet"
        className="w-full max-w-4xl mx-auto h-auto max-h-[140px] sm:max-h-[160px] md:max-h-[180px] aspect-[36/11] block select-none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
      >
        <defs>
          {/* ── Pipe body gradient — light top, darker bottom for 3-D look ── */}
          <linearGradient id={pipeBodyGrad} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f8fafc" />
            <stop offset="18%" stopColor="#e2e8f0" />
            <stop offset="50%" stopColor="#cbd5e1" />
            <stop offset="82%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#64748b" />
          </linearGradient>

          {/* ── Highlight strip ── */}
          <linearGradient id={pipeHighlight} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>

          {/* ── End-cap ellipse gradient ── */}
          <radialGradient id={capGrad} cx="40%" cy="35%" r="65%">
            <stop offset="0%" stopColor="#e2e8f0" />
            <stop offset="60%" stopColor="#94a3b8" />
            <stop offset="100%" stopColor="#475569" />
          </radialGradient>

          {/* ── Chrome joint band ── */}
          <linearGradient id={jointGrad} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#f1f5f9" />
            <stop offset="20%" stopColor="#cbd5e1" />
            <stop offset="50%" stopColor="#94a3b8" />
            <stop offset="80%" stopColor="#64748b" />
            <stop offset="100%" stopColor="#475569" />
          </linearGradient>

          {/* ── Joint highlight ── */}
          <linearGradient id={jointHighlight} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.6" />
            <stop offset="40%" stopColor="#ffffff" stopOpacity="0.1" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>

          {/* ── Shadow beneath pipes ── */}
          <linearGradient id={shadowGrad} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1e293b" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#1e293b" stopOpacity="0" />
          </linearGradient>

          {/* ── Clip paths so lines stay inside tubes ── */}
          <clipPath id={leftPipeClip}>
            <rect x="72" y="62" width="276" height="96" />
          </clipPath>
          <clipPath id={rightPipeClip}>
            <rect x="372" y="62" width="276" height="96" />
          </clipPath>
        </defs>

        {/* ════════════════════════════════════
            BACKGROUND / card feel
        ════════════════════════════════════ */}
        <rect x="0" y="0" width="720" height="220" rx="12" fill="#f0f4f8" />

        {/* ════════════════════════════════════
            SHADOW BELOW BOTH PIPES
        ════════════════════════════════════ */}
        <ellipse cx="360" cy="172" rx="290" ry="14" fill={`url(#${shadowGrad})`} />

        {/* ════════════════════════════════════
            LEFT PIPE BARREL
        ════════════════════════════════════ */}
        <rect x="72" y="62" width="276" height="96" fill={`url(#${pipeBodyGrad})`} />
        <rect x="72" y="62" width="276" height="32" fill={`url(#${pipeHighlight})`} opacity="0.7" />
        <line x1="72" y1="62" x2="348" y2="62" stroke="#b2bec8" strokeWidth="1" />
        <line x1="72" y1="158" x2="348" y2="158" stroke="#475569" strokeWidth="1" />

        {/* ── GREEN reference seam line (top of left barrel) ── */}
        <line
          x1="72"
          y1="67"
          x2="348"
          y2="67"
          stroke="#22c55e"
          strokeWidth="5"
          strokeLinecap="round"
          clipPath={`url(#${leftPipeClip})`}
        />

        {/* ════════════════════════════════════
            LEFT END CAP (circular, left side)
        ════════════════════════════════════ */}
        <ellipse cx="72" cy="110" rx="12" ry="48" fill={`url(#${capGrad})`} />
        <ellipse cx="72" cy="110" rx="12" ry="48" fill="none" stroke="#94a3b8" strokeWidth="1.5" />
        <ellipse cx="72" cy="110" rx="6" ry="34" fill="#1e293b" opacity="0.55" />
        <ellipse cx="72" cy="110" rx="5" ry="33" fill="#0f172a" opacity="0.7" />
        <ellipse cx="69" cy="97" rx="2" ry="8" fill="#ffffff" opacity="0.25" />

        {/* ════════════════════════════════════
            CHROME JOINT BAND (centre)
        ════════════════════════════════════ */}
        <rect x="345" y="54" width="30" height="112" rx="3" fill={`url(#${jointGrad})`} />
        <rect x="345" y="54" width="4" height="112" rx="2" fill="#1e293b" opacity="0.15" />
        <rect x="371" y="54" width="4" height="112" rx="2" fill="#1e293b" opacity="0.10" />
        <rect x="349" y="54" width="12" height="112" rx="2" fill={`url(#${jointHighlight})`} />
        <rect x="345" y="54" width="30" height="112" rx="3" fill="none" stroke="#64748b" strokeWidth="1.2" />

        {/* ════════════════════════════════════
            RIGHT PIPE BARREL
        ════════════════════════════════════ */}
        <rect x="372" y="62" width="276" height="96" fill={`url(#${pipeBodyGrad})`} />
        <rect x="372" y="62" width="276" height="32" fill={`url(#${pipeHighlight})`} opacity="0.7" />
        <line x1="372" y1="62" x2="648" y2="62" stroke="#b2bec8" strokeWidth="1" />
        <line x1="372" y1="158" x2="648" y2="158" stroke="#475569" strokeWidth="1" />

        {/* ── YELLOW dashed free-end seam line ── */}
        <line
          x1="372"
          y1="67"
          x2="648"
          y2="67"
          stroke="#eab308"
          strokeWidth="4"
          strokeDasharray="14 9"
          strokeLinecap="round"
          clipPath={`url(#${rightPipeClip})`}
        />

        {/* ════════════════════════════════════
            RIGHT END CAP
        ════════════════════════════════════ */}
        <ellipse cx="648" cy="110" rx="12" ry="48" fill={`url(#${capGrad})`} />
        <ellipse cx="648" cy="110" rx="12" ry="48" fill="none" stroke="#94a3b8" strokeWidth="1.5" />
        <ellipse cx="648" cy="110" rx="6" ry="34" fill="#1e293b" opacity="0.55" />
        <ellipse cx="648" cy="110" rx="5" ry="33" fill="#0f172a" opacity="0.7" />
        <ellipse cx="645" cy="97" rx="2" ry="8" fill="#ffffff" opacity="0.25" />

        {/* ════════════════════════════════════
            NAV CHEVRONS
        ════════════════════════════════════ */}
        <g
          opacity={prevDisabled ? 0.3 : 0.85}
          style={{ cursor: onPrev && !prevDisabled ? 'pointer' : 'default' }}
          onClick={() => {
            if (!prevDisabled && onPrev) onPrev();
          }}
        >
          <circle cx="30" cy="110" r="20" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />
          <path d="M36 110 L24 104 L24 116 Z" fill="#94a3b8" />
          <circle cx="30" cy="110" r="28" fill="transparent" />
        </g>

        <g
          opacity={nextDisabled ? 0.3 : 0.85}
          style={{ cursor: onNext && !nextDisabled ? 'pointer' : 'default' }}
          onClick={() => {
            if (!nextDisabled && onNext) onNext();
          }}
        >
          <circle cx="690" cy="110" r="20" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1.5" />
          <path d="M684 110 L696 104 L696 116 Z" fill="#94a3b8" />
          <circle cx="690" cy="110" r="28" fill="transparent" />
        </g>

        {/* ════════════════════════════════════
            INFO / HELP ICONS (bottom-right)
        ════════════════════════════════════ */}
        <g transform="translate(628, 180)" opacity={0.75}>
          <circle cx="10" cy="10" r="9" fill="#fff" stroke="#cbd5e1" strokeWidth="1" />
          <text x="10" y="14" textAnchor="middle" fontSize="11" fontWeight="700" fill="#64748b">
            i
          </text>
          <circle cx="34" cy="10" r="9" fill="#fff" stroke="#cbd5e1" strokeWidth="1" />
          <text x="34" y="14" textAnchor="middle" fontSize="11" fontWeight="600" fill="#64748b">
            ?
          </text>
        </g>
      </svg>

      {/* ════════════════════════════════════
          STATUS PILL  (OPTIMAL FIT / REVIEW)
      ════════════════════════════════════ */}
      <div className="pointer-events-none absolute left-1/2 bottom-2 -translate-x-1/2 flex justify-center px-2">
        <div
          className={cn(
            'rounded-full px-5 py-1.5 text-[11px] sm:text-xs font-bold tracking-wide shadow border flex items-center gap-1.5',
            showOptimal
              ? 'bg-emerald-600 text-white border-emerald-700'
              : 'bg-amber-500 text-white border-amber-600'
          )}
        >
          {showOptimal && (
            <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 shrink-0" fill="none" aria-hidden>
              <circle cx="8" cy="8" r="7" stroke="white" strokeWidth="1.5" />
              <path
                d="M4.5 8.5 L7 11 L11.5 6"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          {showOptimal ? 'OPTIMAL FIT' : 'REVIEW'} | MAX HILO: {hiloText}
        </div>
      </div>
    </div>
  );
};

export default OptifitJointPipeSvg;
